import PropTypes from 'prop-types'
import { Component, createRef } from 'react'
import { FormattedMessage } from 'react-intl'

import Result from '@/components/Form/AutoComplete/Result'

import messages from './messages'

import './index.scss'

export default class List extends Component<any, any> {
  static displayName = 'List'

  static propTypes = {
    isSearching: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onListScroll: PropTypes.func.isRequired,
    renderResult: PropTypes.func.isRequired,
    results: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired }))
  }

  static defaultProps = {
    isSearching: false,
    results: null
  }

  ref = createRef()

  /**
   * @see {@link InputList#onBlur}
   */
  onMouseDown = () => this.props.onListScroll()

  /**
   * Sets the maximum height of the result list to whatever vertical height is remaining under the input within the
   * viewport. There is no way to do this with pure CSS given our page structure so must be done dynamically using
   * React's DOM reference system. If the input is at the viewport margin, a minimum height is instead applied equal to
   * the height of two result elements. Scrollbars apply to result lists with items extending beyond this height.
   *
   * @param {HTMLElement} [element] - The result list DOM element. React will set to `null` under certain scenarios.
   */
  setMaxHeightByPosition = (element: HTMLDivElement) => {
    if (element) {
      // how much height is left within the viewport
      const remainingHeight =
        // interoperable viewport height estimate
        Math.max(document.documentElement.clientHeight, window.innerHeight || 0) -
        // height from top of viewport to top of result list element
        element.getBoundingClientRect().top -
        // create a margin on the viewport bottom, equivalent to 1rem
        14

      // at a minimum two results should be shown within the list
      // if the minimum can't be calculated for some reason a default of 100px is chosen
      const resultElement = element.querySelector('.autocomplete__result')
      const minimumHeight = resultElement ? resultElement.clientHeight * 2 : 100

      element.style.maxHeight = `${Math.max(minimumHeight, remainingHeight)}px`
    }
  }

  wrap = (children: any) => (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="field__autocomplete autocomplete" onMouseDown={this.onMouseDown} ref={this.setMaxHeightByPosition}>
      {children}
    </div>
  )

  render() {
    const { isSearching, onChange, renderResult, results } = this.props

    if (isSearching) {
      return this.wrap(
        <Result key="loading" className="autocomplete__result--no-highlight">
          <div className="autocomplete__loading-icon" />
          <FormattedMessage {...messages.searching} />
        </Result>
      )
    }

    if (!results || results.length === 0) {
      return this.wrap(
        <Result key="noresults" className="autocomplete__result--no-highlight">
          <FormattedMessage {...messages.noResults} />
        </Result>
      )
    }

    return this.wrap(
      results.map((result: any) => (
        <Result key={result.id} data={result} onChange={onChange}>
          {renderResult(result)}
        </Result>
      ))
    )
  }
}
